// frontend/src/pages/admin/ManageTagsPage.tsx
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Tag as TagIconLucide, PlusCircle, MoreHorizontal, Search, Edit3, Trash2, Loader2, AlertCircle, FileWarning, LayoutDashboard, Save } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog'; // Assuming you have this
import { Label } from '@/components/ui/label';

import { type TagPublic, type TagCreatePayload, type TagUpdatePayload } from '@/types';
import { getAllTags, createTag, updateTag, deleteTag as apiDeleteTag } from '@/api/tagService';

const ManageTagsPage = () => {
  const [tags, setTags] = useState<TagPublic[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false); // For CUD operations
  const [error, setError] = useState<string | null>(null);

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentTag, setCurrentTag] = useState<TagPublic | null>(null); // For editing
  const [tagNameInput, setTagNameInput] = useState(''); // For form input

  const fetchTags = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const fetchedTags = await getAllTags();
      let tagsToDisplay = fetchedTags;
      if (searchTerm) {
        tagsToDisplay = fetchedTags.filter(tag =>
          tag.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      setTags(tagsToDisplay);
    } catch (err: any) {
      setError("Failed to fetch tags. Please try again later.");
      console.error("Error fetching tags:", err);
      setTags([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      fetchTags();
    }, 300);
    return () => clearTimeout(debounceTimeout);
  }, [fetchTags]);

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  const handleOpenDialog = (tag: TagPublic | null = null) => {
    setCurrentTag(tag);
    setTagNameInput(tag ? tag.name : '');
    setError(null); // Clear previous dialog errors
    setIsDialogOpen(true);
  };

  const handleDialogSubmit = async () => {
    if (!tagNameInput.trim()) {
      setError("Tag name cannot be empty.");
      return;
    }
    setIsProcessing(true);
    setError(null);
    try {
      if (currentTag) { // Editing existing tag
        const payload: TagUpdatePayload = { name: tagNameInput.trim() };
        await updateTag(currentTag.id, payload);
      } else { // Creating new tag
        const payload: TagCreatePayload = { name: tagNameInput.trim() };
        await createTag(payload);
      }
      setIsDialogOpen(false);
      fetchTags(); // Refresh tag list
    } catch (err: any) {
      console.error("Error saving tag:", err);
      setError(err.response?.data?.detail || "Failed to save tag. Name might already exist or be invalid.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteTag = async (tagId: number) => {
    if (!window.confirm("Are you sure you want to delete this tag? This may affect articles and research items using it.")) {
      return;
    }
    setIsProcessing(true); // You might want a specific deleting state like isDeleting[tagId] for per-row loader
    setError(null);
    try {
      await apiDeleteTag(tagId);
      fetchTags(); // Refresh tag list
    } catch (err: any) {
      console.error("Failed to delete tag:", err);
      setError(err.response?.data?.detail || "Failed to delete tag.");
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading && tags.length === 0) {
    return (
      <div className="container mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-lg text-muted-foreground">Loading tags...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 md:py-12 space-y-8 text-foreground bg-background">
      <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-border/60 pb-6 relative">
        <div className="absolute bottom-0 left-0 w-1/3 h-px bg-gradient-to-r from-primary/70 via-accent/50 to-transparent"></div>
        <div className="flex items-center">
          <TagIconLucide className="h-10 w-10 text-primary mr-4 flex-shrink-0" />
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Manage <span className="text-gradient-maroon-to-gold">Tags</span>
            </h1>
            <p className="mt-1 text-muted-foreground">
              Create, edit, and manage tags for articles and research.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center mt-4 sm:mt-0">
          <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10 hover:text-primary transition-colors self-start sm:self-center w-full sm:w-auto">
            <Link to="/admin/dashboard">
              <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
            </Link>
          </Button>
          <Button onClick={() => handleOpenDialog()} className="!bg-gradient-to-r !from-primary !to-accent hover:!from-primary/90 hover:!to-accent/90 text-primary-foreground shadow-md hover:shadow-lg transition-all duration-300 w-full sm:w-auto cursor-pointer">
            <PlusCircle className="mr-2 h-5 w-5" /> Add New Tag
          </Button>
        </div>
      </header>

      <div className="flex flex-col sm:flex-row items-center gap-4">
        <div className="relative w-full sm:max-w-sm">
          <Input
            type="search"
            placeholder="Search tags by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-border focus:border-primary focus:ring-primary"
          />
          <Search className="absolute left-3 top-1/2 h-5 w-5 text-muted-foreground -translate-y-1/2" />
        </div>
      </div>

      {error && !isLoading && tags.length === 0 && ( // Page-level error if initial load fails
        <div className="container mx-auto px-4 py-20 text-center min-h-[calc(60vh-10rem)] flex flex-col justify-center items-center">
          <FileWarning className="h-16 w-16 text-destructive mx-auto mb-6" />
          <h2 className="text-2xl font-semibold text-destructive mb-2">Error Loading Tags</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={fetchTags} variant="outline">Try Again</Button>
        </div>
      )}
      
      {error && !isDialogOpen && ( // General error display outside dialog
         <div className="p-3 bg-destructive/10 text-destructive border border-destructive/30 rounded-md flex items-center">
           <AlertCircle className="h-5 w-5 mr-2" /> {error}
         </div>
      )}

      <Card className="shadow-lg bg-card border border-border/30 pt-0">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-vedasMaroon-800">
              <TableRow className="border-border/50">
                <TableHead className="w-[50%] text-primary-foreground py-4">Tag Name</TableHead>
                <TableHead className="text-primary-foreground">Created At</TableHead>
                {/* Add more columns like 'Usage Count' if available from backend */}
                <TableHead className="text-right text-primary-foreground">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && tags.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-16 text-muted-foreground">
                    <div className="flex flex-col items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
                      Loading tags...
                    </div>
                  </TableCell>
                </TableRow>
              ) : tags.length > 0 ? (
                tags.map((tag) => (
                  <TableRow key={tag.id} className="border-border/40 hover:bg-muted/30 dark:hover:bg-muted/10">
                    <TableCell className="font-medium text-foreground">{tag.name}</TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(tag.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild className="justify-self-end cursor-pointer">
                            {isProcessing && currentTag?.id === tag.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-popover text-popover-foreground border-border">
                          <DropdownMenuLabel>Actions for "{tag.name}"</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => handleOpenDialog(tag)} className="hover:bg-muted/50 dark:hover:bg-muted/20 focus:bg-muted/50 dark:focus:bg-muted/20 cursor-pointer">
                            <Edit3 className="mr-2 h-4 w-4 text-primary/80" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-border/50" />
                          <DropdownMenuItem
                            onClick={() => handleDeleteTag(tag.id)}
                            className="text-destructive focus:text-destructive-foreground focus:bg-destructive/90 cursor-pointer"
                            disabled={isProcessing && currentTag?.id === tag.id}
                          >
                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-16 text-muted-foreground">
                    No tags found. {searchTerm && "Try adjusting your search."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog for Create/Edit Tag */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[425px] bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">{currentTag ? 'Edit Tag' : 'Create New Tag'}</DialogTitle>
            <DialogDescription>
              {currentTag ? `Update the name for the tag "${currentTag.name}".` : 'Enter the name for the new tag.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {error && isDialogOpen && ( // Display errors specific to dialog operations
                <div className="p-3 bg-destructive/10 text-destructive border border-destructive/30 rounded-md flex items-center text-sm">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" /> {error}
                </div>
            )}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="tagName" className="text-right text-foreground">
                Name
              </Label>
              <Input
                id="tagName"
                value={tagNameInput}
                onChange={(e) => setTagNameInput(e.target.value)}
                className="col-span-3 !bg-input border-border focus:border-primary focus:ring-primary"
                disabled={isProcessing}
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={isProcessing} className='cursor-pointer'>Cancel</Button>
            </DialogClose>
            <Button type="button" onClick={handleDialogSubmit} disabled={isProcessing || !tagNameInput.trim()} className="!bg-gradient-to-r !from-primary !to-accent hover:!from-primary/90 hover:!to-accent/90 text-primary-foreground cursor-pointer">
              {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {currentTag ? 'Save Changes' : 'Create Tag'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageTagsPage;